import {DashboardOutlined, ReloadOutlined} from '@ant-design/icons';
import {Alert, Button, Card, Flex, Progress, Space, Spin, theme, Typography} from 'antd';
import React from 'react';

import {ApiQuery} from '../../../gen/api';
import {formatAgo, parseMoney} from '../dashboard/helpers/slowSelling';

const {Text} = Typography;

/** eBay's numbers change only as we list and sell: no need to re-read them on every visit. */
const LIMIT_STALE_MS = 5 * 60_000;
/** Below this share of the limit left, the bar turns red. */
const LOW_SHARE = 0.1;

const errorMessage = (errors: Array<{message?: string}>) => errors?.[0]?.message;

const formatCount = (value: number) => value.toLocaleString('en-US');
const formatAmount = (value: number) =>
    `$${value.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;

/** One limit as Seller Hub shows it: what is left, a bar of what is used, used / limit. */
const LimitRow: React.FC<{
    limit?: number;
    remaining?: number;
    format: (value: number) => string;
    noun: string;
}> = ({limit, remaining, format, noun}) => {
    const {token} = theme.useToken();
    if (limit === undefined) {
        return <Text type="secondary">No monthly cap on the {noun}.</Text>;
    }
    if (remaining === undefined) {
        return (
            <Text>
                Limit: <Text strong>{format(limit)}</Text> {noun} a month
            </Text>
        );
    }
    const used = Math.max(limit - remaining, 0);
    const low = remaining < limit * LOW_SHARE;
    return (
        <Flex vertical>
            <Text style={{fontSize: 18, fontWeight: 600}}>
                {format(remaining)} more{noun === 'items' ? ' items' : ''}
            </Text>
            <Progress
                percent={limit > 0 ? (used / limit) * 100 : 100}
                showInfo={false}
                strokeColor={low ? token.colorError : token.colorSuccess}
                style={{marginBlock: 4}}
            />
            <Text type="secondary">
                {format(used)} listed and sold / {format(limit)} limit
            </Text>
        </Flex>
    );
};

/** The account's monthly selling limit and what is left of it, read from eBay. */
export const SellingLimitCard: React.FC = () => {
    const limitQuery = ApiQuery.useAdminEbaySellingLimitQuery({staleTime: LIMIT_STALE_MS});
    const limit = limitQuery.data;

    return (
        <Card
            title={
                <Space>
                    <DashboardOutlined />
                    Monthly selling limits
                </Space>
            }
            extra={
                <Space>
                    {limit && <Text type="secondary">checked {formatAgo(limit.checkedAt)}</Text>}
                    <Button
                        size="small"
                        icon={<ReloadOutlined />}
                        loading={limitQuery.isFetching}
                        onClick={() => limitQuery.refetch()}
                    />
                </Space>
            }
        >
            {limitQuery.isLoading && <Spin />}
            {limitQuery.isError && (
                <Alert
                    type="error"
                    showIcon
                    message="Failed to read the selling limit from eBay"
                    description={errorMessage(limitQuery.error)}
                />
            )}
            {limit && (
                <Flex
                    vertical
                    gap={16}
                >
                    <Flex
                        gap={32}
                        wrap
                    >
                        <div style={{flex: '1 1 260px', maxWidth: 480}}>
                            <LimitRow
                                // The API sends null for "no cap"; the generated type says undefined.
                                limit={limit.quantityLimit ?? undefined}
                                remaining={limit.quantityRemaining ?? undefined}
                                format={formatCount}
                                noun="items"
                            />
                        </div>
                        <div style={{flex: '1 1 260px', maxWidth: 480}}>
                            <LimitRow
                                limit={parseMoney(limit.amountLimit)}
                                remaining={parseMoney(limit.amountRemaining)}
                                format={formatAmount}
                                noun="total amount"
                            />
                        </div>
                    </Flex>
                    {limit.remainingError && (
                        <Alert
                            type="info"
                            showIcon
                            message="eBay did not say what is left of the limit this month"
                            description={limit.remainingError}
                        />
                    )}
                    <Text type="secondary">
                        Counts everything listed and sold this calendar month, Seller Hub listings included; resets on
                        the 1st. Publishing fails once a limit is reached. Without an eBay Store, 250 listings a month
                        are free to list (renewals of Good &apos;Til Cancelled listings count too).
                    </Text>
                </Flex>
            )}
        </Card>
    );
};
